import { faSignal1, faSignal2, faSignal3, faStopwatch } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";

import { BreadcrumbInput } from "@/mk2/apps/blogs/components/ArticleHeader";
import {
    AdvancedArticleCategoryEntity,
    BlogPostEntity,
    HashtagEntity,
} from "@/mk2/apps/blogs/schemas";
import {
    HOWTOS_SLUG,
    RECIPES_SLUG,
    advancedArticleUrl,
    blogsAdvancedArticlePreviewGridUrl,
} from "@/mk2/apps/blogs/urls";
import { getAdvnacedArticleBreadcrumbs } from "@/mk2/apps/blogs/utils";
import { UserChip } from "@/mk2/apps/forum/components/UserChip";
import { getBestVersionForPreview } from "@/mk2/apps/home/components/HomepagePost2";
import { BreadcrumbSiteStructure } from "@/mk2/components/BreadcrumbSiteStructure";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Dot } from "@/mk2/components/Dot";
import { ExpandableClamp } from "@/mk2/components/ExpandableClamp";
import { ImgResponsive } from "@/mk2/components/ImgResponsive";
import { Link } from "@/mk2/components/Link";
import { LoadingPlaceholder } from "@/mk2/components/LoadingPlaceholder";
import { RenderAST } from "@/mk2/components/RenderAST";
import Sticker, { StickerSize } from "@/mk2/components/Sticker";
import { TrackClick } from "@/mk2/components/TrackClick";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { ShowPhotosAndVideos } from "@/mk2/helpers/article_ast/filters";
import { getAllPlaintext } from "@/mk2/helpers/article_ast/utils";
import { url } from "@/mk2/helpers/urls";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { interpolate } from "@/mk2/services/i18n";
import {
    ADVANCED_ARTICLES_PREVIEW_GRID_HASHTAGS_TITLE,
    ADVANCED_ARTICLES_PREVIEW_GRID_SUBTITLE_count_HOWTOS,
    ADVANCED_ARTICLES_PREVIEW_GRID_SUBTITLE_count_RECIPES,
    BOOKMARKED_HOWTOS,
    BOOKMARKED_RECIPES,
    DRAFT_HOWTOS,
    DRAFT_RECIPES,
    MY_HOWTOS,
    MY_RECIPES,
    NO_RESULTS,
    PB_MENU_HOWTOS,
    PB_MENU_RECIPES,
    SHOW_MORE,
    count_HOWTOS,
    count_RECIPES,
    number_MINUTES,
} from "@/mk/autogenerated/translation";
import { isAuthenticated } from "@/mk/bazaar/common/userUtils";

import styles from "./AdvancedArticlesPreviewGrid.mscss";
import { Props } from "./AdvancedArticlesPreviewGridPage";

function specialTitle(specialSlug: string, mainCategorySlug: string | undefined | null) {
    if (!specialSlug || !mainCategorySlug) {
        return null;
    }

    const map = {
        drafts: {
            [RECIPES_SLUG]: DRAFT_RECIPES,
            [HOWTOS_SLUG]: DRAFT_HOWTOS,
        },
        bookmarks: {
            [RECIPES_SLUG]: BOOKMARKED_RECIPES,
            [HOWTOS_SLUG]: BOOKMARKED_HOWTOS,
        },
    };

    return map[specialSlug]?.[mainCategorySlug] ?? null;
}

function getBreadcrumbItems(breadcrumbInputs: BreadcrumbInput[]) {
    const breadcrumbItems = [];
    return breadcrumbInputs.reduce((acc, { itemUrl, itemLabel }, index) => {
        if (itemUrl) {
            acc.push(
                <Link className={styles.BreadcrumbItem} key={index} to={itemUrl}>
                    {itemLabel}
                </Link>,
            );
        } else {
            acc.push(<React.Fragment key={index}>{itemLabel}</React.Fragment>);
        }
        return acc;
    }, breadcrumbItems);
}

export default class AdvancedArticlesPreviewGrid extends React.PureComponent<Props> {
    public componentDidMount() {
        const { mainCategorySlug, subCategorySlug, hashtag, page, onStoreEvent } = this.props;
        if (mainCategorySlug === RECIPES_SLUG) {
            onStoreEvent("recipes_grid_view", {
                category_slug: subCategorySlug || mainCategorySlug,
                hashtag: hashtag || null,
                page,
            });
        }
    }

    public render() {
        const {
            loadingState,
            category,
            mainCategorySlug,
            posts,
            showCategoryTabs,
            showHashtags,
            subCategories,
            categoryHashtags,
            categoryContentItem,
            subCategorySlug,
            username,
            special,
            hashtag,
            isAdvancedArticleHomepage,
            createBtnLabel,
            createBtnUrl,
            requestUser,
        } = this.props;

        const breadcrumbInputs = getAdvnacedArticleBreadcrumbs(category, username, hashtag);

        const hasData = !!posts && posts.length > 0;

        const stuff = mainCategorySlug === RECIPES_SLUG ? PB_MENU_RECIPES : PB_MENU_HOWTOS;
        const heroSectionTitle =
            specialTitle(special, mainCategorySlug) ||
            (username && `${stuff} od @${username}`) ||
            (hashtag && `${stuff} s #${hashtag}`) ||
            category?.name;
        const heroSectionSubtitle = !posts
            ? null
            : isAdvancedArticleHomepage
              ? category?.id === 1
                  ? interpolate(ADVANCED_ARTICLES_PREVIEW_GRID_SUBTITLE_count_RECIPES, {
                        count: posts.length,
                    })
                  : category?.id === 2
                    ? interpolate(ADVANCED_ARTICLES_PREVIEW_GRID_SUBTITLE_count_HOWTOS, {
                          count: posts.length,
                      })
                    : null
              : category && mainCategorySlug === RECIPES_SLUG
                ? `(${interpolate(count_RECIPES, { count: posts.length })})`
                : category && mainCategorySlug === HOWTOS_SLUG
                  ? `(${interpolate(count_HOWTOS, { count: posts.length })})`
                  : null;

        return (
            <div className={styles.AdvancedArticlesPreviewGrid}>
                {!isAdvancedArticleHomepage &&
                    (loadingState === LoadingState.LOADING ? (
                        <div className={styles.AdvancedArticlesPreviewGrid__breadcrumbs}>
                            <LoadingPlaceholder width={65} />
                        </div>
                    ) : (
                        !!breadcrumbInputs?.length && (
                            <BreadcrumbSiteStructure
                                items={getBreadcrumbItems(breadcrumbInputs)}
                                className={styles.AdvancedArticlesPreviewGrid__breadcrumbs}
                            />
                        )
                    ))}
                <HeroSection
                    mainCategorySlug={mainCategorySlug}
                    subCategorySlug={subCategorySlug}
                    isAdvancedArticleHomepage={isAdvancedArticleHomepage}
                    title={heroSectionTitle}
                    subtitle={heroSectionSubtitle}
                />
                <div
                    className={
                        isAdvancedArticleHomepage
                            ? styles.AdvancedArticlesPreviewGrid__shadowPanel
                            : undefined
                    }
                >
                    {isAdvancedArticleHomepage &&
                        (loadingState === LoadingState.LOADING ? (
                            <div className={styles.AdvancedArticlesPreviewGrid__categoryTabs}>
                                <LoadingPlaceholder
                                    width={"100%"}
                                    height={140}
                                    style={{ opacity: "0.5", margin: "5px 10px" }}
                                />
                            </div>
                        ) : (
                            showCategoryTabs && (
                                <div className={styles.AdvancedArticlesPreviewGrid__categoryTabs}>
                                    <CategoryTabs
                                        subCategories={subCategories}
                                        categorySlug={mainCategorySlug}
                                    />
                                </div>
                            )
                        ))}
                    {categoryContentItem?.contentASTAdditionalData ||
                    categoryContentItem?.content ? (
                        <div className={styles.AdvancedArticlesPreviewGrid__categoryContent}>
                            <ExpandableClamp expandLabel={SHOW_MORE} clamp={4}>
                                {!!categoryContentItem.contentAST ? (
                                    <RenderAST
                                        contentAST={categoryContentItem.contentAST}
                                        linkedObjects={
                                            categoryContentItem.contentASTAdditionalData
                                                .linkedObjects
                                        }
                                        showPhotosAndVideos={ShowPhotosAndVideos.ALL}
                                        renderSpecialWikiLinks={false}
                                        photos={categoryContentItem.photos}
                                    />
                                ) : (
                                    !!categoryContentItem.content && (
                                        <div>{categoryContentItem.content}</div>
                                    )
                                )}
                            </ExpandableClamp>
                        </div>
                    ) : (
                        !isAdvancedArticleHomepage &&
                        loadingState === LoadingState.LOADING && (
                            <div className={styles.AdvancedArticlesPreviewGrid__categoryContent}>
                                <LoadingPlaceholder
                                    width={"100%"}
                                    height={75}
                                    style={{ opacity: "0.5", marginBottom: 5 }}
                                />
                            </div>
                        )
                    )}
                    {isAdvancedArticleHomepage &&
                        (loadingState === LoadingState.LOADING ? (
                            <div className={styles.AdvancedArticlesPreviewGrid__categoryContent}>
                                <LoadingPlaceholder
                                    width={"100%"}
                                    height={25}
                                    style={{ opacity: "0.5", marginBottom: 5 }}
                                />
                            </div>
                        ) : (
                            <CTAAddRecipe
                                createBtnLabel={createBtnLabel}
                                createBtnUrl={createBtnUrl}
                                mainCategorySlug={mainCategorySlug}
                            />
                        ))}
                    {loadingState === LoadingState.LOADING ? (
                        <div className={styles.AdvancedArticlesPreviewGrid__categoryContent}>
                            <LoadingPlaceholder
                                width={"100%"}
                                height={60}
                                style={{ opacity: "0.5", marginBottom: 5 }}
                            />
                        </div>
                    ) : (
                        showHashtags && (
                            <Hashtags
                                title={ADVANCED_ARTICLES_PREVIEW_GRID_HASHTAGS_TITLE}
                                hashtags={categoryHashtags}
                                isAdvancedArticleHomepage={isAdvancedArticleHomepage}
                                mainCategorySlug={mainCategorySlug}
                            />
                        )
                    )}
                    {requestUser &&
                        isAuthenticated(requestUser) &&
                        isAdvancedArticleHomepage &&
                        (loadingState === LoadingState.LOADING ? (
                            <div className={styles.AdvancedArticlesPreviewGrid__mySites}>
                                <LoadingPlaceholder
                                    width={"100%"}
                                    height={20}
                                    style={{ opacity: "0.5", margin: "5px 10px" }}
                                />
                            </div>
                        ) : (
                            showCategoryTabs &&
                            (category && mainCategorySlug === RECIPES_SLUG ? (
                                <div className={styles.AdvancedArticlesPreviewGrid__mySites}>
                                    <Btn
                                        label={MY_RECIPES}
                                        link={url(blogsAdvancedArticlePreviewGridUrl, {
                                            advancedArticleCategorySlug: RECIPES_SLUG,
                                            flag1: "u",
                                            value1: requestUser.username,
                                        })}
                                        type={BtnType.BlueOutline}
                                    />
                                    <Btn
                                        label={BOOKMARKED_RECIPES}
                                        link={url(blogsAdvancedArticlePreviewGridUrl, {
                                            advancedArticleCategorySlug: RECIPES_SLUG,
                                            flag1: "s",
                                            value1: "bookmarks",
                                        })}
                                        type={BtnType.BlueOutline}
                                    />
                                    <Btn
                                        label={DRAFT_RECIPES}
                                        link={url(blogsAdvancedArticlePreviewGridUrl, {
                                            advancedArticleCategorySlug: RECIPES_SLUG,
                                            flag1: "s",
                                            value1: "drafts",
                                        })}
                                        type={BtnType.BlueOutline}
                                    />
                                </div>
                            ) : (
                                <div className={styles.AdvancedArticlesPreviewGrid__mySites}>
                                    <Btn
                                        label={MY_HOWTOS}
                                        link={url(blogsAdvancedArticlePreviewGridUrl, {
                                            advancedArticleCategorySlug: HOWTOS_SLUG,
                                            flag1: "u",
                                            value1: requestUser.username,
                                        })}
                                        type={BtnType.BlueOutline}
                                    />
                                    <Btn
                                        label={BOOKMARKED_HOWTOS}
                                        link={url(blogsAdvancedArticlePreviewGridUrl, {
                                            advancedArticleCategorySlug: HOWTOS_SLUG,
                                            flag1: "s",
                                            value1: "bookmarks",
                                        })}
                                        type={BtnType.BlueOutline}
                                    />
                                    <Btn
                                        label={DRAFT_HOWTOS}
                                        link={url(blogsAdvancedArticlePreviewGridUrl, {
                                            advancedArticleCategorySlug: HOWTOS_SLUG,
                                            flag1: "s",
                                            value1: "drafts",
                                        })}
                                        type={BtnType.BlueOutline}
                                    />
                                </div>
                            ))
                        ))}
                </div>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.handleLoad}
                    onRenderSuccess={this.renderSuccess}
                    onRenderInit={this.renderInit}
                    hasData={hasData}
                />
            </div>
        );
    }

    private renderInit = () => {
        return (
            <div className={styles.AdvancedArticlesPreviewGrid__posts}>
                <div className={styles.AdvancedArticlesPreviewGrid__card}>
                    <LoadingPlaceholder
                        width={"100%"}
                        height={200}
                        style={{ opacity: "0.5", marginBottom: 5 }}
                    />
                    <div className={styles.AdvancedArticlesPreviewGrid__card__title}>
                        <LoadingPlaceholder width={"100%"} height={20} style={{ opacity: "0.5" }} />
                    </div>
                    <div className={styles.AdvancedArticlesPreviewGrid__card__meta}>
                        <LoadingPlaceholder width={60} height={16} style={{ opacity: "0.5" }} />
                        <LoadingPlaceholder width={60} height={16} style={{ opacity: "0.5" }} />
                        <LoadingPlaceholder width={60} height={16} style={{ opacity: "0.5" }} />
                    </div>
                </div>
                <div className={styles.AdvancedArticlesPreviewGrid__card}>
                    <LoadingPlaceholder
                        width={"100%"}
                        height={200}
                        style={{ opacity: "0.5", marginBottom: 5 }}
                    />
                    <div className={styles.AdvancedArticlesPreviewGrid__card__title}>
                        <LoadingPlaceholder width={"100%"} height={20} style={{ opacity: "0.5" }} />
                    </div>
                    <div className={styles.AdvancedArticlesPreviewGrid__card__meta}>
                        <LoadingPlaceholder width={60} height={16} style={{ opacity: "0.5" }} />
                        <LoadingPlaceholder width={60} height={16} style={{ opacity: "0.5" }} />
                        <LoadingPlaceholder width={60} height={16} style={{ opacity: "0.5" }} />
                    </div>
                </div>
                <div className={styles.AdvancedArticlesPreviewGrid__card}>
                    <LoadingPlaceholder
                        width={"100%"}
                        height={200}
                        style={{ opacity: "0.5", marginBottom: 5 }}
                    />
                    <div className={styles.AdvancedArticlesPreviewGrid__card__title}>
                        <LoadingPlaceholder width={"100%"} height={20} style={{ opacity: "0.5" }} />
                    </div>
                    <div className={styles.AdvancedArticlesPreviewGrid__card__meta}>
                        <LoadingPlaceholder width={60} height={16} style={{ opacity: "0.5" }} />
                        <LoadingPlaceholder width={60} height={16} style={{ opacity: "0.5" }} />
                        <LoadingPlaceholder width={60} height={16} style={{ opacity: "0.5" }} />
                    </div>
                </div>
            </div>
        );
    };

    private renderSuccess = () => {
        const { posts } = this.props;

        if (!posts || posts.length === 0) {
            return (
                <div className={styles.AdvancedArticlesPreviewGrid__noPosts}>
                    <Sticker lazy id={38} size={StickerSize.Bigger} />
                    <div>{NO_RESULTS}</div>
                </div>
            );
        }

        return (
            <div className={styles.AdvancedArticlesPreviewGrid__posts}>
                {posts.map((post) => (
                    <GridRecipeCard key={post.id} post={post} />
                ))}
            </div>
        );
    };

    private handleLoad = () => {
        const { onLoad, username, mainCategorySlug, subCategorySlug, hashtag, special, page } =
            this.props;
        onLoad(username, mainCategorySlug, subCategorySlug, hashtag, special, page);
    };
}

const HeroSection: React.FC<{
    mainCategorySlug: string;
    subCategorySlug: string;
    isAdvancedArticleHomepage: boolean;
    title: string;
    subtitle: string;
}> = ({ mainCategorySlug, subCategorySlug, isAdvancedArticleHomepage, title, subtitle }) => {
    const categorySlug = subCategorySlug || mainCategorySlug;
    return (
        <div
            className={cx(
                isAdvancedArticleHomepage
                    ? styles.AdvancedArticlesPreviewGrid__hero__mainCategory
                    : styles.AdvancedArticlesPreviewGrid__hero__subCategory,
                isAdvancedArticleHomepage &&
                    styles[`AdvancedArticlesPreviewGrid__hero--${categorySlug}`],
            )}
        >
            <div className={styles.AdvancedArticlesPreviewGrid__hero__inner}>
                <h1 className={styles.AdvancedArticlesPreviewGrid__hero__title}>
                    {title || (
                        <div style={{ display: "inline-block" }}>
                            <LoadingPlaceholder
                                width={120}
                                height={33}
                                style={{ opacity: "0.5" }}
                            />
                        </div>
                    )}{" "}
                    <span className={styles.AdvancedArticlesPreviewGrid__hero__subtitle}>
                        {subtitle || (
                            <div style={{ display: "inline-block" }}>
                                <LoadingPlaceholder
                                    width={150}
                                    height={17}
                                    style={{ opacity: "0.5" }}
                                />
                            </div>
                        )}
                    </span>
                </h1>
            </div>
        </div>
    );
};

const CategoryTabs: React.FC<{
    activeSlug?: string;
    categorySlug: string;
    subCategories: AdvancedArticleCategoryEntity[];
}> = ({ categorySlug, subCategories, activeSlug }) => {
    return (
        <div className={styles.AdvancedArticlesPreviewGrid__categoryTabs__scroll}>
            {subCategories.map((subCategory) => {
                const link = (
                    <Link
                        className={
                            subCategory.slug === activeSlug
                                ? styles.AdvancedArticlesPreviewGrid__categoryTabs__item__active
                                : styles.AdvancedArticlesPreviewGrid__categoryTabs__item
                        }
                        to={url(blogsAdvancedArticlePreviewGridUrl, {
                            advancedArticleCategorySlug: categorySlug,
                            flag1: "c",
                            value1: subCategory.slug,
                        })}
                    >
                        {subCategory.name}
                    </Link>
                );
                return categorySlug === RECIPES_SLUG ? (
                    <TrackClick
                        key={subCategory.slug}
                        name="recipes_category_click"
                        props={{ category_slug: subCategory.slug }}
                    >
                        {link}
                    </TrackClick>
                ) : (
                    <React.Fragment key={subCategory.slug}>{link}</React.Fragment>
                );
            })}
        </div>
    );
};

const Hashtags: React.FC<{
    mainCategorySlug: string;
    isAdvancedArticleHomepage: boolean;
    title: string;
    hashtags: HashtagEntity[];
}> = ({ mainCategorySlug, hashtags, title, isAdvancedArticleHomepage }) => {
    if (!hashtags || hashtags.length === 0) return null;
    return (
        <div
            className={cx(
                styles.AdvancedArticlesPreviewGrid__hashtags,
                isAdvancedArticleHomepage && styles.AdvancedArticlesPreviewGrid__hashtags__center,
            )}
        >
            <h3>{title}</h3>
            <div className={styles.AdvancedArticlesPreviewGrid__hashtags__list}>
                {hashtags.map((hashtag) => (
                    <Link
                        key={hashtag.label}
                        to={url(blogsAdvancedArticlePreviewGridUrl, {
                            advancedArticleCategorySlug: mainCategorySlug,
                            flag1: "h",
                            value1: hashtag.label,
                        })}
                    >
                        #{hashtag.label}
                    </Link>
                ))}
            </div>
        </div>
    );
};

const CTAAddRecipe: React.FC<{
    createBtnLabel: string;
    createBtnUrl: string;
    mainCategorySlug: string;
}> = ({ createBtnLabel, createBtnUrl, mainCategorySlug }) => {
    const btn = <Btn link={createBtnUrl} label={createBtnLabel} />;
    return (
        <div className={styles.AdvancedArticlesPreviewGrid__cta}>
            {mainCategorySlug === RECIPES_SLUG ? (
                <TrackClick name="recipes_create_click" props={{ source: "grid" }}>
                    {btn}
                </TrackClick>
            ) : (
                btn
            )}
        </div>
    );
};

// TODO GABO recipes - rename i18n

function difficultyLabel(level: number) {
    switch (level) {
        case 1:
            return "Ľahké";
        case 2:
            return "Stredné";
        case 3:
            return "Ťažké";
        default:
            return "Neznáme";
    }
}

export const GridRecipeCard: React.FC<{
    post: BlogPostEntity;
    hideUser?: boolean;
    oneliner?: React.ReactChild[];
    coverPhotoMaxHeight?: number;
}> = ({ post, hideUser, oneliner, coverPhotoMaxHeight = 200 }) => {
    const title = post && getAllPlaintext(post?.titleAST);
    const coverPhoto = post?.advancedArticle?.coverPhoto;
    const user = post?.user;
    const cookTime = post?.advancedArticle?.cookTimeMinutes;
    const difficulty = post?.advancedArticle?.difficultyLevel;
    const postAdvancedArticleUrl = advancedArticleUrl(post);
    return (
        <div className={styles.AdvancedArticlesPreviewGrid__card}>
            {/* Cover image */}
            <div className={styles.AdvancedArticlesPreviewGrid__card__image}>
                {coverPhoto && (
                    <Link to={postAdvancedArticleUrl}>
                        <ImgResponsive
                            photo={coverPhoto}
                            photoVersion={getBestVersionForPreview(coverPhoto)}
                            alt={title}
                            maxHeight={coverPhotoMaxHeight}
                        />
                    </Link>
                )}

                {user && !hideUser && (
                    <div className={styles.AdvancedArticlesPreviewGrid__card__userBadge}>
                        <UserChip user={user} size={24} spaceBetween={4} />
                    </div>
                )}
            </div>

            <div className={styles.AdvancedArticlesPreviewGrid__card__title}>
                <Link to={postAdvancedArticleUrl}>{title}</Link>
            </div>

            <Link
                to={postAdvancedArticleUrl}
                className={styles.AdvancedArticlesPreviewGrid__card__meta}
            >
                {cookTime ? (
                    <div className={styles.AdvancedArticlesPreviewGrid__card__metaItem}>
                        <FontAwesomeIcon icon={faStopwatch} />
                        &nbsp;
                        {interpolate(number_MINUTES, { count: cookTime, number: cookTime })}
                    </div>
                ) : null}

                {difficulty !== null && (
                    <div className={styles.AdvancedArticlesPreviewGrid__card__metaItem}>
                        <Difficulty level={difficulty} />
                        &nbsp;
                        {difficultyLabel(difficulty)}
                    </div>
                )}

                {!!post.starsRatingCount && (
                    <div className={styles.AdvancedArticlesPreviewGrid__card__metaItem}>
                        ⭐ &nbsp;
                        <b>
                            {post.starsRatingScore?.toLocaleString("sk-SK", {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1,
                            })}
                        </b>
                        <span
                            className={
                                styles["AdvancedArticlesPreviewGrid__card__metaItem--ratingCount"]
                            }
                        >
                            <Dot spaces={2} />
                            {post.starsRatingCount}×
                        </span>
                    </div>
                )}
            </Link>

            {oneliner && (
                <Link
                    to={postAdvancedArticleUrl}
                    className={styles.AdvancedArticlesPreviewGrid__card__oneliner}
                >
                    {oneliner}
                </Link>
            )}
        </div>
    );
};

const levelIcons = {
    1: faSignal1,
    2: faSignal2,
    3: faSignal3,
};

export function Difficulty({ level }) {
    const Icon = levelIcons[level];

    return (
        <div style={{ position: "relative", width: "1em", height: "1em" }}>
            {level < 3 && (
                <FontAwesomeIcon
                    icon={faSignal3}
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        color: "#ccc",
                    }}
                />
            )}

            <FontAwesomeIcon
                icon={Icon}
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    color: "#000",
                }}
            />
        </div>
    );
}
